import { useEffect, useRef, useState } from 'react';
import { format, isToday, parseISO, isBefore, startOfDay } from 'date-fns';
import styles from './events-widget.module.scss';
import {
  DatePicker,
  Typography,
  Icon,
  Button,
  ICONS_ENUM,
  IconButton
} from '@sanlam-glacier/app-common/components';
// import AddEventForm from './AddEventForm';
import { atom, useAtom } from 'jotai';
import { dashboardType } from 'stores/dashboard-store/dashboard-store';
import { eventsData } from 'stores/mockData';
import React from 'react';
import WidgetLoader from '../../../../atoms/widget-loader/WidgetLoader';
import { Event } from 'modules/dashboard/types/widgets/widget.interfaces';
import { EventType } from 'modules/dashboard/types/widgets/widget.types';

const initialEvents: Event[] = eventsData;
const isLoadingAtom = atom(true);

const EventsWidget = () => {
  const [events] = useState<Event[]>(initialEvents);
  const [filterToday, setFilterToday] = useState(false);
  const [selectedDate] = useState<Date | null>(null);
  const [dashType] = useAtom(dashboardType);
  const [isLoading, setIsLoading] = useAtom(isLoadingAtom);
  const hasEventsToday = events.some((event) => isToday(parseISO(event.date)));
  const todayDate = parseISO(new Date().toISOString());
  const isSameDay = (date1: Date, date2: Date) =>
    format(date1, 'yyyy-MM-dd') === format(date2, 'yyyy-MM-dd');
  const eventsListRef = useRef<HTMLUListElement>(null);
  const testComponentName = 'Events';

  const scrollOffset = 0;

  const filteredEvents = events.filter((event) => {
    const eventDate = parseISO(event.date);

    if (dashType !== 'intermediary' && event.type === 'birthday') {
      return false;
    }

    if (filterToday) {
      return isToday(eventDate);
    }

    if (selectedDate) {
      return isSameDay(eventDate, selectedDate);
    }

    return true;
  });

  const scrollToToday = () => {
    if (eventsListRef.current) {
      const today = format(new Date(), 'yyyy-MM-dd');
      const todayElement = eventsListRef.current.querySelector(`[data-date="${today}"]`);
      const noEventsElement = eventsListRef.current.querySelector(
        `[data-testid="${testComponentName}NoEventsText"]`
      );

      const elementToScroll = hasEventsToday ? todayElement : noEventsElement;

      if (elementToScroll) {
        const container = eventsListRef.current;
        const elementRect = elementToScroll.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        const scrollPosition =
          container.scrollTop + (elementRect.top - containerRect.top) - scrollOffset;

        container.scrollTo({
          top: scrollPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  const scrollToSelectedDate = (selectedDate: Date) => {
    if (!eventsListRef.current) return;

    const dateKey = format(selectedDate, 'yyyy-MM-dd');
    const dateElement = eventsListRef.current.querySelector(`[data-date="${dateKey}"]`);

    if (dateElement) {
      const container = eventsListRef.current;
      const elementRect = dateElement.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      const scrollPosition = container.scrollTop + (elementRect.top - containerRect.top);

      container.scrollTo({
        top: scrollPosition,
        behavior: 'smooth'
      });
    }
  };

  const eventsByDate = filteredEvents.reduce<Record<string, Event[]>>((acc, event) => {
    const dateKey = format(parseISO(event.date), 'yyyy-MM-dd');
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(event);
    return acc;
  }, {});

  const sortedDateKeys = Object.keys(eventsByDate).sort();

  const isDateInPast = (dateToCheck: string) => {
    const today = startOfDay(new Date());
    const checkDate = startOfDay(parseISO(dateToCheck));
    return isBefore(checkDate, today);
  };

  const getEventStyle = (type: EventType) => {
    switch (type) {
      case 'birthday':
        return styles.birthday;
      case 'holiday':
        return styles.holiday;
      case 'event':
        return styles.event;
      default:
        return '';
    }
  };

  const findDateSplitIndex = (dates: string[]) => {
    const today = startOfDay(new Date());
    return dates.findIndex((date) => !isBefore(startOfDay(parseISO(date)), today));
  };

  const splitIndex = findDateSplitIndex(sortedDateKeys);

  const formatDateHeading = (dateKey: string) => {
    const date = parseISO(dateKey);
    const currentYear = new Date().getFullYear();
    const dateYear = date.getFullYear();
    const baseFormat = 'EEE, d MMM';

    return format(date, dateYear > currentYear ? `${baseFormat} yyyy` : baseFormat);
  };

  const onFilterTodayChange = (value: boolean) => {
    setFilterToday(value);
  };

  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
      setTimeout(() => {
        scrollToToday();
      }, 100);
    }, 2500);
  }, []);

  return (
    <>
      {isLoading ? (
        <WidgetLoader />
      ) : (
        <div className={styles.container}>
          <div className={styles.controls}>
            <div className={styles.controlButtonContainer}>
              {!selectedDate && (
                <Button
                  data-testid={testComponentName + 'TodayBtn'}
                  variant={Button.VARIANT.SECONDARY}
                  className={styles.todayBtn}
                  onClick={scrollToToday}
                  size={Button.SIZE.SMALL}
                >
                  Today - {formatDateHeading(format(todayDate, 'yyyy-MM-dd'))}
                </Button>
              )}
            </div>

            {dashType === 'intermediary' && (
              <DatePicker
                data-testid={testComponentName + 'DatePicker'}
                customInput={
                  <div className={styles.datePickerIcon}>
                    <IconButton variant={IconButton.VARIANT.TERTIARY} size={IconButton.SIZE.SMALL}>
                      <Icon icon={ICONS_ENUM.ACTION_2_CALENDAR_TODAY} />
                    </IconButton>{' '}
                  </div>
                }
                className={styles.datePicker}
                shouldCloseOnSelect={true}
                fullWidth={true}
                variant={DatePicker.VARIANT.DEFAULT}
                date={selectedDate || new Date()}
                onChange={(date) => {
                  const newDate = Array.isArray(date) ? date[0] : date;
                  onFilterTodayChange(false);

                  if (newDate) {
                    setTimeout(() => {
                      scrollToSelectedDate(newDate);
                    }, 100);
                  }
                }}
              />
            )}
          </div>

          <ul
            className={`${styles.eventsList} ${dashType === 'client' ? styles.client : ''}`}
            data-testid={testComponentName + 'EventList'}
            ref={eventsListRef}
          >
            {sortedDateKeys.map((dateKey, index) => (
              <React.Fragment key={dateKey}>
                {index === splitIndex && !hasEventsToday && (
                  <div
                    className={styles.noEventsText}
                    data-split-index={splitIndex}
                    data-testid={testComponentName + 'NoEvents'}
                  >
                    <Typography
                      color={Typography.COLOR.PRIMARY}
                      variant={Typography.VARIANT.B3}
                      weight={Typography.WEIGHT.BOLD}
                      className={`${styles.dateHeading} ${
                        isDateInPast(dateKey) ? styles.pastDate : ''
                      }`}
                      data-testid={testComponentName + 'TodayText'}
                    >
                      Today - {formatDateHeading(format(todayDate, 'yyyy-MM-dd'))}
                    </Typography>
                    <Typography
                      variant={Typography.VARIANT.B4}
                      weight={Typography.WEIGHT.NORMAL}
                      data-testid={testComponentName + 'NoEventsText'}
                    >
                      No events today
                    </Typography>
                  </div>
                )}
                <li
                  data-date={dateKey}
                  className={styles.eventsListItem}
                  data-testid={testComponentName + 'EventItem'}
                >
                  <Typography
                    variant={Typography.VARIANT.B3}
                    weight={Typography.WEIGHT.MEDIUM}
                    className={`${styles.dateHeading} ${
                      isDateInPast(dateKey) ? styles.pastDate : ''
                    }`}
                  >
                    {formatDateHeading(dateKey)}
                  </Typography>
                  {(eventsByDate[dateKey] ?? []).map((event) => (
                    <div
                      key={event.id}
                      className={`${styles.eventItem} ${getEventStyle(
                        event.type
                      )} ${isDateInPast(event.date) ? styles.pastDate : ''}`}
                    >
                      <span className={styles.highlight} />
                      <div className={styles.eventText}>
                        <Typography
                          variant={Typography.VARIANT.B4}
                          weight={Typography.WEIGHT.MEDIUM}
                        >
                          {event.title}
                        </Typography>
                        {event.subtext && (
                          <div className={styles.eventSubtext}>{event.subtext}</div>
                        )}
                      </div>
                      {dashType === 'intermediary' && event.type != 'holiday' && (
                        <Icon
                          icon={ICONS_ENUM.NAVIGATION_ARROW_FORWARD}
                          className={styles.eventArrow}
                        />
                      )}
                    </div>
                  ))}
                </li>
              </React.Fragment>
            ))}
          </ul>

          {/* For V2 */}

          {/* <AddEventForm
          onAdd={(newEvent) =>
            setEvents((prev) => [
              ...prev,
              {
                ...newEvent,
                id: Date.now().toString(),
                type: 'custom',
              },
            ])
          }
        /> */}
        </div>
      )}
    </>
  );
};

export default EventsWidget;
